import { useRef, useEffect, useState } from "react";
import { Check, X, Zap, Target, Smile, DollarSign, Headphones, Sparkles } from "lucide-react";

const comparisons = [
  {
    feature: "Rapidité",
    icon: Zap,
    floop: { check: true, text: "Ultra rapide (<3s)" },
    others: { check: false, text: "10-30 secondes" },
  },
  {
    feature: "Précision",
    icon: Target,
    floop: { check: true, text: "99.2% de précision" },
    others: { check: false, text: "~70% de précision" },
  },
  {
    feature: "Simplicité",
    icon: Smile,
    floop: { check: true, text: "Zéro apprentissage" },
    others: { check: false, text: "Courbe complexe" },
  },
  {
    feature: "Prix",
    icon: DollarSign,
    floop: { check: true, text: "À partir de 0 FCFA" },
    others: { check: false, text: "15-50€/mois min" },
  },
  {
    feature: "Support",
    icon: Headphones,
    floop: { check: true, text: "WhatsApp instantané" },
    others: { check: false, text: "Email 48-72h" },
  },
];

export const ComparisonSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-1/3 right-0 w-96 h-96 bg-primary/5 blur-3xl rounded-full" />
      <div className="absolute bottom-1/3 left-0 w-96 h-96 bg-accent/5 blur-3xl rounded-full" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <span 
            className={`inline-flex items-center gap-2 text-sm font-medium text-primary mb-4 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Comparaison
          </span>
          <h2 
            className={`text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 font-display transition-all duration-700 delay-100 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Pourquoi Floop est{" "}
            <span className="bg-gradient-floop bg-clip-text text-transparent">différent</span>
          </h2>
          <p 
            className={`text-lg text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Découvrez ce qui nous distingue des autres solutions IA du marché.
          </p>
        </div>

        <div 
          className={`max-w-4xl mx-auto transition-all duration-700 delay-300 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Header */}
          <div className="grid grid-cols-3 gap-4 mb-6 px-6">
            <div className="text-sm font-medium text-muted-foreground">Critère</div>
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-gradient-floop text-white px-5 py-2 rounded-full text-sm font-bold shadow-lg shadow-primary/30">
                <Sparkles className="w-4 h-4" />
                Floop
              </div>
            </div>
            <div className="text-center">
              <span className="text-sm font-medium text-muted-foreground">Autres IA</span>
            </div>
          </div>

          {/* Comparison rows */}
          <div className="space-y-3">
            {comparisons.map((item, index) => (
              <div
                key={index}
                className={`relative transition-all duration-500 ${
                  isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
                }`}
                style={{ transitionDelay: `${400 + index * 100}ms` }}
                onMouseEnter={() => setHoveredRow(index)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                {/* Row glow on hover */}
                <div 
                  className={`absolute -inset-1 bg-gradient-floop rounded-2xl blur opacity-0 transition-opacity duration-300 ${
                    hoveredRow === index ? "opacity-10" : ""
                  }`}
                />
                
                <div className={`relative grid grid-cols-3 gap-4 items-center bg-card/80 backdrop-blur-sm border rounded-2xl p-5 transition-all duration-300 ${
                  hoveredRow === index ? "border-primary/50 shadow-lg" : "border-border/50"
                }`}>
                  {/* Feature */}
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
                      hoveredRow === index ? "bg-gradient-floop shadow-lg" : "bg-secondary"
                    }`}>
                      <item.icon className={`w-6 h-6 transition-colors ${
                        hoveredRow === index ? "text-white" : "text-primary"
                      }`} />
                    </div>
                    <span className="font-semibold text-foreground">{item.feature}</span>
                  </div>
                  
                  {/* Floop */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-success/20 flex items-center justify-center">
                      <Check className="w-5 h-5 text-success" />
                    </div>
                    <span className="text-sm text-foreground font-medium text-center">{item.floop.text}</span>
                  </div>
                  
                  {/* Others */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-destructive/20 flex items-center justify-center">
                      <X className="w-5 h-5 text-destructive" />
                    </div>
                    <span className="text-sm text-muted-foreground text-center">{item.others.text}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div 
            className={`mt-12 text-center transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
            style={{ transitionDelay: "900ms" }}
          >
            <p className="text-muted-foreground mb-2">
              <span className="text-foreground font-semibold">5/5</span> avantages en faveur de Floop
            </p>
            <div className="inline-flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="w-8 h-2 rounded-full bg-gradient-floop" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
